import React, { useEffect, useRef, useState } from 'react';
import debounce from 'debounce';
import { isEmpty } from 'ramda';
import { userStore } from '../../store';

import api from '../../api';
import MultiSelectDropdown, {
    type DropdownOptionType,
    type MultiSelectDropdownProps
} from '../multiSelectDropdown/MultiSelectDropdown';

import { useTranslation } from 'react-i18next';
import { isNotEmpty } from '../../js/fpUtils';
import helpers from '../../js/helpers';

import './DealershipDropdown.scss';
import { SEARCH_DEBOUNCE_VALUE } from '../../js/_constants';

type OwnProps = {
    defaultOptions?: DropdownOptionType[];
    defaultValue?: DropdownOptionType[];
    isMultiSelect?: boolean;
    kvpsOnly?: boolean;
    onChange: (selectedOptions: DropdownOptionType[]) => void;
} & Partial<MultiSelectDropdownProps>;

type DealershipDropdownProps = OwnProps;

const DealershipDropdown = ({
    defaultOptions,
    defaultValue,
    isMultiSelect,
    onChange,
    kvpsOnly,
    ...multiSelectDropdownProps
}: DealershipDropdownProps) => {
    const { t } = useTranslation();
    const { details } = userStore();
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState<DropdownOptionType[]>([]);
    const initialOptions = useRef<DropdownOptionType[]>([]);

    const fetchAndSetOptions = async (query?: string) => {
        /**
         * WHen user deletes a query, we can immediately fallback to the initial options.
         * Initial options are either defaultOptions passed in as props,
         * or search results of the first search when component mounts
         */
        if (isNotEmpty(initialOptions.current) && query === '') {
            return setOptions(initialOptions.current);
        }

        setIsLoading(true);

        const params = {
            brand: details.userGrantedBrands,
            ...(query && {
                title: query,
                kvps: query
            })
        };

        const dealershipsCollection = await api.user.getUserDealerships({
            params
        });

        const dealershipOptions = helpers.mapDealershipOptions(
            dealershipsCollection['hydra:member'], kvpsOnly
        );

        if (initialOptions.current && isEmpty(initialOptions.current)) {
            initialOptions.current = dealershipOptions;
        }

        setOptions(dealershipOptions);
        setIsLoading(false);

        return dealershipOptions;
    };

    useEffect(() => {
        /**
         * When component mounts, we either set defaultOptions to be the first options to show,
         * or if there is no defaultOptions, a search query with empty query string will be triggered.
         */
        if (defaultOptions && isNotEmpty(defaultOptions)) {
            setIsLoading(true);
            setOptions(defaultOptions);

            if (initialOptions.current && isEmpty(initialOptions.current)) {
                initialOptions.current = defaultOptions;
            }

            setIsLoading(false);
        } else {
            fetchAndSetOptions();
        }
    }, []);

    const handleDealershipSelection = (
        selectedOptions: DropdownOptionType[]
    ) => {
        onChange(selectedOptions);
    };

    const handleQueryChange = async (query: string) => {
        fetchAndSetOptions(query);
    };

    const debouncedQueryChange = debounce(
        handleQueryChange,
        SEARCH_DEBOUNCE_VALUE
    );

    return (
        <MultiSelectDropdown
            isFilterable
            isLoading={isLoading}
            isSingleSelect={!isMultiSelect}
            defaultValue={defaultValue}
            label={t('dealership')+ ' *'}
            maxOptionsLength={50}
            name="dealership-dropdown"
            onChange={handleDealershipSelection}
            onQueryChange={debouncedQueryChange}
            options={options}
            searchPlaceholder="Search for dealerships"
            {...multiSelectDropdownProps}
        />
    );
};

export default DealershipDropdown;
